/**
 * dview:AreaLocationViewer: an object with an area location along its tracks (a bridge, a tunnel).
 * The tracks are lanes on one km axis; tracks with the same name that continue each other share a
 * lane. The structure is drawn where the topology puts it on the tracks (their associated-track
 * pieces), with the data's length; the objects on those tracks inside it can be shown by kind.
 * Nothing is computed from coordinates here: every km is read from the graph.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@bn-apps/dim-ui-react';
import { FitGlyph, Legend, LegendToggle } from './legend';
import { useStored } from '../stored';
import { uriToDereferencePath } from '@utils/prefix.utils';
import { pickLang, setting, settingList, type ViewPropertyShape } from '@utils/viewShapes.utils';
import { keyedValues } from '../page';
import { formatKm, localName, num, words } from '../linearLocation';
import { Glyph, ticksFor } from './alongTrack';
import { useNumberFormat } from './figures';
import { firstKm } from './line';
import { neighbourSymbol } from './spot';
import { recordName, UriLink, uriText } from './values';
import type { Widget, WidgetContext } from './types';
import styles from './area.module.scss';
import tiles from './widgets.module.scss';
import { SectionSummary, sectionMap, sectionsFor } from './sections';

const TRACK = 'http://ontology.railml.org/railml3#Track';
const LANE_GAP = 46;
const TOP = 64;

interface Piece {
  from: number;
  to: number;
}

interface Lane {
  name: string;
  tracks: string[];
  /** The name and places of the lane's tracks. */
  labels: { name: string; places: string[] }[];
  pieces: Piece[];
}

interface TrackEntry {
  pieces: Piece[];
  place: string | null;
  designator: string | null;
  begin: number | null;
  end: number | null;
  /** The dates as the data writes them (its own time zone). */
  beginDate: string | null;
  endDate: string | null;
}

const time = (value: string | undefined) => (value ? Date.parse(value) : NaN);

export interface Placed {
  iri: string;
  name: string;
  symbol: string;
  kind: string;
  /** Its classes, for the sections of the contents (dview:sections). */
  types: string[];
  km: number;
  track: string;
}

/** Pieces that touch become one. */
function mergePieces(pieces: Piece[]): Piece[] {
  const sorted = [...pieces].sort((a, b) => a.from - b.from);
  const merged: Piece[] = [];
  for (const piece of sorted) {
    const last = merged[merged.length - 1];
    if (last && piece.from <= last.to + 1) last.to = Math.max(last.to, piece.to);
    else merged.push({ ...piece });
  }
  return merged;
}

/**
 * What an object is, as specific as the data says: its railml3:hasType when that is a defined kind (a speed signal, a milepost),
 * else its most specific class with a view shape (Signal, not Functional infrastructure entity).
 */
function objectKind(kind: string | undefined, types: string[], context: WidgetContext): string | null {
  if (kind) {
    const name = context.resolved[kind]?.[0] ?? words(localName(kind));
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }
  // A class's own node shape lists its superclasses' rows too, so the one with the most rows is the most specific
  const shapes = types
    .map((type) => context.model?.shapes?.nodeShapes.find((node) => node.targetClasses.includes(type)))
    .filter((shape): shape is NonNullable<typeof shape> => Boolean(shape))
    .sort((a, b) => b.properties.length - a.properties.length);
  return shapes[0] ? pickLang(shapes[0].label, context.language) : null;
}

type AreaLayout = NonNullable<ReturnType<typeof computeLayout>>;

// The drawing, its contents list, its search text and the facts all ask for the same layout: worked
// out once per page model and set of names
const layouts = new WeakMap<object, WeakMap<object, Map<string, AreaLayout | null>>>();

export function areaLayout(property: ViewPropertyShape, context: WidgetContext, untyped: string): AreaLayout | null {
  const model = context.model;
  if (!model) return null;
  const byNames = layouts.get(model) ?? new WeakMap<object, Map<string, AreaLayout | null>>();
  layouts.set(model, byNames);
  const cache = byNames.get(context.resolved) ?? new Map<string, AreaLayout | null>();
  byNames.set(context.resolved, cache);
  const key = `${property.id} ${untyped} ${context.language}`;
  if (!cache.has(key)) cache.set(key, computeLayout(property, context, untyped));
  return cache.get(key)!;
}

/**
 * Where the structure lies on its tracks and what is inside it: the lanes, the km it covers, and the
 * objects on its tracks within that km. Shared by the drawing and dview:ContentsViewer.
 */
function computeLayout(property: ViewPropertyShape, context: WidgetContext, untyped: string) {
  const model = context.model;
  const table = model?.tables[property.id];
  const kmFrom = firstKm(context, property, 'kmFrom');
  const kmTo = firstKm(context, property, 'kmTo');
  if (!model || kmFrom === null || kmTo === null) return null;
  const kind = keyedValues(model, setting(property, 'value') ?? '')[0]?.value ?? '';
  const length = num(keyedValues(model, setting(property, 'length') ?? '')) ?? Math.abs(kmTo - kmFrom);

  const trackShape = model.shapes?.nodeShapes.find((node) => node.targetClasses.includes(TRACK));
  const trackClass = trackShape ? pickLang(trackShape.label, context.language) : null;

  // Each track's pieces inside the structure, and where the track belongs (its station or line section)
  const byTrack = new Map<string, TrackEntry>();
  for (const item of table?.items ?? []) {
    const track = item.cells.track?.find((value) => value.type === 'uri')?.value;
    if (!track) continue;
    const entry = byTrack.get(track) ?? {
      pieces: [],
      place: null,
      designator: item.cells['track-designator']?.[0]?.value ?? null,
      begin: time(item.cells['track-begin']?.[0]?.value) || null,
      end: time(item.cells['track-end']?.[0]?.value) || null,
      beginDate: item.cells['track-begin']?.[0]?.value.slice(0, 10) ?? null,
      endDate: item.cells['track-end']?.[0]?.value.slice(0, 10) ?? null,
    };
    const from = num(item.cells.from);
    const to = num(item.cells.to);
    if (from !== null && to !== null) entry.pieces.push({ from: Math.min(from, to), to: Math.max(from, to) });
    const place = item.cells.station?.[0]?.value ?? item.cells.section?.[0]?.value ?? null;
    entry.place ??= place ? uriText(place, context) : null;
    byTrack.set(track, entry);
  }
  // Without an associated track the structure's own km stand for every track it is on
  if (byTrack.size === 0) {
    for (const value of keyedValues(model, 'on-track')) {
      if (value.type === 'uri') byTrack.set(value.value, { pieces: [{ from: kmFrom, to: kmTo }], place: null, designator: null, begin: null, end: null, beginDate: null, endDate: null });
    }
  }
  // A track version that has ended (an end date in the past) is not drawn; the facts list every version
  const now = Date.now();
  const periods = [...byTrack.entries()].map(([iri, entry]) => ({
    iri,
    begin: entry.begin,
    end: entry.end,
    beginDate: entry.beginDate,
    endDate: entry.endDate,
    ended: entry.end !== null && entry.end <= now,
  }));
  const ended = [...byTrack.entries()].filter(([, entry]) => entry.end !== null && entry.end <= now).map(([track]) => track);
  if (ended.length < byTrack.size) for (const track of ended) byTrack.delete(track);
  // Tracks with the same name where one ends as the next begins share a lane
  const lanes: Lane[] = [];
  const start = (pieces: Piece[]) => Math.min(...pieces.map((piece) => piece.from));
  const end = (pieces: Piece[]) => Math.max(...pieces.map((piece) => piece.to));
  for (const [track, entry] of [...byTrack.entries()].sort((a, b) => start(a[1].pieces) - start(b[1].pieces))) {
    const name = recordName(track, context, trackClass);
    const lane = lanes.find((candidate) => candidate.name === name && Math.abs(end(candidate.pieces) - start(entry.pieces)) <= 1);
    if (lane) {
      lane.tracks.push(track);
      lane.pieces = mergePieces([...lane.pieces, ...entry.pieces]);
      const places = lane.labels[lane.labels.length - 1].places;
      if (entry.place && !places.includes(entry.place)) places.push(entry.place);
    } else {
      lanes.push({ name, tracks: [track], labels: [{ name, places: entry.place ? [entry.place] : [] }], pieces: mergePieces(entry.pieces) });
    }
  }
  lanes.sort((a, b) => a.name.localeCompare(b.name));
  const laneOf = new Map(lanes.flatMap((lane, index) => lane.tracks.map((track) => [track, index] as const)));

  // The structure where the topology puts it; its own km only when no track says
  const pieces = lanes.flatMap((lane) => lane.pieces);
  const lo = pieces.length > 0 ? start(pieces) : Math.min(kmFrom, kmTo);
  const hi = pieces.length > 0 ? end(pieces) : Math.max(kmFrom, kmTo);
  // The objects on these tracks inside it, at their km on that track
  const objects: Placed[] = [];
  for (const key of settingList(property, 'objects')) {
    const row = model.rows[key];
    for (const item of (row && model.tables[row.id]?.items) ?? []) {
      if (item.iri === model.uri) continue;
      const km = num(item.cells['track-at']) ?? num(item.cells.at);
      if (km === null || km < lo || km > hi) continue;
      const kindOf = objectKind(item.cells.kind?.find((value) => value.type === 'uri')?.value, (item.cells.type ?? []).map((value) => value.value), context) ?? untyped;
      for (const value of item.cells.track ?? []) {
        if (!laneOf.has(value.value)) continue;
        objects.push({
          iri: item.iri,
          name: uriText(item.iri, context),
          symbol: neighbourSymbol(item, row!, context),
          kind: kindOf,
          types: (item.cells.type ?? []).map((type) => type.value),
          km,
          track: value.value,
        });
      }
    }
  }
  return { kmFrom, kmTo, kind, length, lanes, laneOf, lo, hi, objects, periods };
}

/** A lane's places, the first and the last when there are more than two. */
const placesText = (places: string[]) => (places.length > 2 ? `${places[0]} … ${places[places.length - 1]}` : places.join(' → '));

/** The kinds of the objects with their symbol, most first; an object on several tracks counts once. */
function countKinds(objects: Placed[]): { name: string; symbol: string; types: string[]; iris: string[]; count: number }[] {
  const byKind = new Map<string, { name: string; symbol: string; types: string[]; iris: string[]; count: number }>();
  for (const object of objects) {
    const entry = byKind.get(object.kind) ?? { name: object.kind, symbol: object.symbol, types: object.types, iris: [], count: 0 };
    if (!entry.iris.includes(object.iri)) entry.iris.push(object.iri);
    entry.count = entry.iris.length;
    byKind.set(object.kind, entry);
  }
  return [...byKind.values()].sort((a, b) => b.count - a.count);
}

const Area = ({ property, context }: Parameters<Widget>[0]): JSX.Element | null => {
  const { t } = useTranslation();
  const format = useNumberFormat();
  const navigate = useNavigate();
  const stageRef = useRef<HTMLDivElement>(null);
  const wheel = useRef<(event: WheelEvent) => void>(() => undefined);
  const [width, setWidth] = useState(0);
  // A topic picked in the side menu other than the contents fades the objects; the structure stays in focus
  // A topic shows its own kinds of objects, all for the contents topic, none for the others
  const topic = context.topic;
  const [legend, setLegend] = useStored('dim-entity-legend', false);
  const inTopic = (symbol: string) => !topic || topic.objects || topic.kinds.includes(symbol);
  const [view, setView] = useState<[number, number] | null>(null);
  const [drag, setDrag] = useState<[number, number] | null>(null);
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const listener = (event: WheelEvent) => wheel.current(event);
    stage.addEventListener('wheel', listener, { passive: false });
    const observer = new ResizeObserver(() => setWidth(stage.clientWidth));
    observer.observe(stage);
    setWidth(stage.clientWidth);
    return () => {
      stage.removeEventListener('wheel', listener);
      observer.disconnect();
    };
  }, []);

  const layout = areaLayout(property, context, t('entity.template.spot.untyped'));
  if (!layout) return null;
  const { kmFrom, kmTo, kind, length, lanes, laneOf, lo, hi, objects } = layout;
  const pad = Math.max(10, (hi - lo) * 0.12);
  const [from, to] = view ?? [lo - pad, hi + pad];

  const kinds = countKinds(objects);
  const shown = objects.filter((object) => inTopic(object.symbol) && object.km >= from && object.km <= to);

  const stageWidth = width || 900;
  const left = 24;
  const right = stageWidth - 24;
  const x = (km: number) => left + ((km - from) / (to - from)) * (right - left);
  const kmAt = (clientX: number) => {
    const box = stageRef.current!.getBoundingClientRect();
    return from + ((clientX - box.left - left) / Math.max(1, right - left)) * (to - from);
  };
  const zoomTo = (a: number, b: number) => {
    if (Math.abs(b - a) >= 2) setView([Math.min(a, b), Math.max(a, b)]);
  };
  wheel.current = (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    const at = kmAt(event.clientX);
    const factor = event.deltaY > 0 ? 1.4 : 1 / 1.4;
    zoomTo(at - (at - from) * factor, at + (to - at) * factor);
  };
  const height = TOP + lanes.length * LANE_GAP + 44;
  const laneY = (index: number) => TOP + 24 + index * LANE_GAP;
  const x0 = x(lo);
  const x1 = x(hi);
  const round = Math.min(14, (x1 - x0) / 2);
  const open = (iri: string) => navigate(uriToDereferencePath(iri));
  const ticks = ticksFor(from, to, Math.max(3, Math.round(stageWidth / 170)));
  const kindName = t(`entity.template.symbol.structure.${kind}`, kind);
  const metres = format(length, Number.isInteger(length) ? 0 : 1);

  return (
    <div className={styles.area}>
      <div className={styles.bar}>
        <div className={styles.toggles}>
          {view && (
            <Button variant="tertiary" size="xs" onClick={() => setView(null)}>
              {t('entity.template.along.resetZoom', { from: formatKm(from), to: formatKm(to) })}
            </Button>
          )}
          {shown.length > 0 && <LegendToggle open={legend} onToggle={setLegend} />}
        </div>
      </div>
      {/* What the marks mean, when asked for; the topic in the side menu decides what is lit */}
      {legend && kinds.length > 0 && (
        <Legend
          label={t('entity.template.along.legend')}
          entries={countKinds(shown).map((entry) => ({
            key: entry.name,
            icon: <FitGlyph symbol={entry.symbol} />,
            label: entry.name,
            count: entry.count,
          }))}
        />
      )}
      <div
        ref={stageRef}
        className={styles.stage}
        onPointerDown={(event) =>
          event.button === 0 && !(event.target as Element).closest('[role="link"]') && setDrag([kmAt(event.clientX), kmAt(event.clientX)])
        }
        onPointerMove={(event) => drag && setDrag([drag[0], kmAt(event.clientX)])}
        onPointerUp={() => {
          if (drag && Math.abs(x(drag[1]) - x(drag[0])) > 4) zoomTo(drag[0], drag[1]);
          setDrag(null);
        }}
        onPointerLeave={() => setDrag(null)}
      >
        <svg
          width={stageWidth}
          height={height}
          viewBox={`0 0 ${stageWidth} ${height}`}
          role="group"
          aria-label={t('entity.template.area.aria', { kind: kindName, length: metres, from: formatKm(kmFrom), to: formatKm(kmTo) })}
        >
          <line x1={x0} y1={30} x2={x0} y2={44} className={styles.dimension} />
          <line x1={x1} y1={30} x2={x1} y2={44} className={styles.dimension} />
          {kind === 'tunnel' ? (
            <path
              d={`M${x0} ${height - 30} V${TOP} Q${x0} ${TOP - 14} ${x0 + round} ${TOP - 14} H${x1 - round} Q${x1} ${TOP - 14} ${x1} ${TOP} V${height - 30}`}
              className={styles.tunnel}
            />
          ) : (
            <g className={styles.bridge}>
              <rect x={x0} y={TOP - 6} width={Math.max(2, x1 - x0)} height={lanes.length * LANE_GAP} />
              <line x1={x0} y1={TOP - 6} x2={x1} y2={TOP - 6} />
              <line x1={x0} y1={TOP - 6 + lanes.length * LANE_GAP} x2={x1} y2={TOP - 6 + lanes.length * LANE_GAP} />
            </g>
          )}
          {lanes.map((lane, index) => {
            const y = laneY(index);
            return (
              <g key={lane.tracks.join(' ')}>
                <line x1={left} y1={y} x2={right} y2={y} className={styles.lane} />
                {lane.pieces.map((piece) => (
                  <rect
                    key={`${piece.from}-${piece.to}`}
                    x={x(piece.from)}
                    y={y - 5}
                    width={Math.max(2, x(piece.to) - x(piece.from))}
                    height={10}
                    className={kind === 'tunnel' ? styles.pieceTunnel : styles.pieceBridge}
                  />
                ))}
                {shown
                  .filter((object) => laneOf.get(object.track) === index)
                  .map((object) => (
                    <g
                      key={`${object.iri}-${object.track}`}
                      className={styles.object}
                      role="link"
                      tabIndex={0}
                      aria-label={`${object.name} · ${object.kind} · km ${formatKm(object.km)}`}
                      onClick={() => open(object.iri)}
                      onKeyDown={(event) => event.key === 'Enter' && open(object.iri)}
                    >
                      <title>{`${object.name} · ${object.kind} · km ${formatKm(object.km)}`}</title>
                      <svg x={x(object.km) - 10} y={y - 22} width="20" height="22" viewBox="0 0 28 30" overflow="visible">
                        <Glyph symbol={object.symbol} x={14} y={29} />
                      </svg>
                    </g>
                  ))}
                {/* Drawn last, so the name stays on top of the lane's symbols */}
                <g
                  className={styles.trackName}
                  role="link"
                  tabIndex={0}
                  onClick={() => open(lane.tracks[0])}
                  onKeyDown={(event) => event.key === 'Enter' && open(lane.tracks[0])}
                >
                  <title>{lane.tracks.map((track) => uriText(track, context)).join(' → ')}</title>
                  <text x={left} y={y - 10}>
                    {lane.labels.map((label, part) => (
                      <tspan key={part}>
                        {part > 0 && <tspan className={styles.place}>{' → '}</tspan>}
                        {label.name}
                        {label.places.length > 0 && <tspan className={styles.place}>{` · ${placesText(label.places)}`}</tspan>}
                      </tspan>
                    ))}
                  </text>
                </g>
              </g>
            );
          })}
          {drag && Math.abs(x(drag[1]) - x(drag[0])) > 4 && (
            <rect x={Math.min(x(drag[0]), x(drag[1]))} y={0} width={Math.abs(x(drag[1]) - x(drag[0]))} height={height} className={styles.zoomBox} />
          )}
          {ticks.map((tick) => (
            <text key={tick} x={x(tick)} y={height - 8} className={styles.tick} textAnchor="middle">
              {formatKm(tick)}
            </text>
          ))}
        </svg>
      </div>
      <p className={styles.note}>{t('entity.template.area.note', { kind: kindName.toLowerCase(), count: new Set(objects.map((object) => object.iri)).size })}</p>
    </div>
  );
};

export const AreaLocationViewer: Widget = (props) => <Area {...props} />;

/** Whether the row has what the structure drawing needs: its own km from and to. */
export const hasArea = (context: WidgetContext, property: Parameters<Widget>[0]['property']) =>
  firstKm(context, property, 'kmFrom') !== null && firstKm(context, property, 'kmTo') !== null;

const CONTENTS_PREVIEW = 4;
// About two rows of tiles in a two-column card before "Show all n kinds"
const KINDS_SHOWN = 6;

/** dview:ContentsViewer: what lies inside the structure of the dview:area row, per kind, with the objects as links. */
const Contents = ({ property, context }: Parameters<Widget>[0]): JSX.Element | null => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [allKinds, setAllKinds] = useState(false);
  const tilesRef = useRef<HTMLDivElement>(null);
  // All kinds need the whole width: the card that holds them says so to the masonry
  useEffect(() => {
    tilesRef.current?.closest('[data-wide]')?.toggleAttribute('data-full', allKinds);
  }, [allKinds]);
  const untypedName = t('entity.template.spot.untyped');
  const area = context.model?.rows[setting(property, 'area') ?? ''];
  const layout = area ? areaLayout(area, context, untypedName) : null;
  if (!layout || layout.objects.length === 0) return null;
  const kinds = countKinds(layout.objects);
  // Tiles like the facilities on a track; an object without a type shows the end of its id
  const shownKinds = allKinds ? kinds : kinds.slice(0, KINDS_SHOWN);
  // The shared sections of the contents (dview:sections on the group): by picture symbol or class, the rest last
  const sectionOf = sectionMap(context, property.groupId);
  const sectionKey = (entry: (typeof kinds)[number]) =>
    sectionOf.get(entry.symbol) ?? entry.types.map((type) => sectionOf.get(type)).find(Boolean) ?? '';
  const sections = sectionOf.size > 0 ? sectionsFor(context, (context.sectionCard ? kinds : shownKinds).map(sectionKey), t('entity.template.facility.other')) : [];
  // Overview: a line per section with its number of objects
  if (context.summary && sectionOf.size > 0) {
    const all = sectionsFor(context, kinds.map(sectionKey), t('entity.template.facility.other'));
    return (
      <SectionSummary
        sections={all.map((section) => ({ ...section, count: kinds.filter((entry) => sectionKey(entry) === section.key).reduce((sum, entry) => sum + entry.count, 0) }))}
      />
    );
  }
  // Without sections: the three largest kinds, the rest counted
  if (context.summary) {
    return (
      <span className={styles.contentsSummary}>
        {kinds.slice(0, 3).map((entry) => (
          <span key={entry.name} className={styles.contentsHead}>
            <FitGlyph symbol={entry.symbol} />
            <span>{entry.name}</span>
            <span className={styles.kindCount}>{entry.count}</span>
          </span>
        ))}
        {kinds.length > 3 && <span className={styles.kindCount}>{t('entity.template.area.moreKinds', { count: kinds.length - 3 })}</span>}
      </span>
    );
  }

  const tile = (entry: (typeof kinds)[number]) => {
    const all = open.has(entry.name);
    const untyped = entry.name === untypedName;
    return (
      <li key={entry.name} className={tiles.facility}>
        <span className={tiles.facilityHead}>
          {context.pictures !== false && <FitGlyph symbol={entry.symbol} />}
          <span className={tiles.facilityLabel}>{entry.name}</span>
          <span className={tiles.facilityState}>{entry.count}</span>
        </span>
        <span className={tiles.facilityItems}>
          {(all ? entry.iris : entry.iris.slice(0, CONTENTS_PREVIEW)).map((iri) => (
            <UriLink key={iri} uri={iri} context={context}>
              {untyped ? `…${localName(iri).slice(-8)}` : undefined}
            </UriLink>
          ))}
          {entry.iris.length > CONTENTS_PREVIEW && (
            <button
              type="button"
              className={tiles.facilityMore}
              aria-expanded={all}
              onClick={() => {
                const next = new Set(open);
                if (all) next.delete(entry.name);
                else next.add(entry.name);
                setOpen(next);
              }}
            >
              {all ? t('entity.template.facts.showFewer') : `+${entry.iris.length - CONTENTS_PREVIEW}`}
            </button>
          )}
        </span>
      </li>
    );
  };
  // On a topic page every section is a card of its own, with all its kinds
  if (context.sectionCard && sections.length > 0) {
    const card = context.sectionCard;
    return (
      <>
        {sections.map((section) => (
          <section key={section.key} className={card.card} aria-label={section.title}>
            <h3 className={card.title}>{section.title}</h3>
            <ul className={tiles.facilities}>{kinds.filter((entry) => sectionKey(entry) === section.key).map(tile)}</ul>
          </section>
        ))}
      </>
    );
  }
  return (
    <div ref={tilesRef} className={tiles.contentsTiles}>
      {sections.length > 0 ? (
        sections.map((section) => (
          <section key={section.key} className={tiles.facilitySection}>
            <h4 className={tiles.facilitySectionTitle}>{section.title}</h4>
            <ul className={tiles.facilities}>{shownKinds.filter((entry) => sectionKey(entry) === section.key).map(tile)}</ul>
          </section>
        ))
      ) : (
        <ul className={tiles.facilities}>{shownKinds.map(tile)}</ul>
      )}
      {kinds.length > KINDS_SHOWN && (
        <button type="button" className={tiles.facilityMore} aria-expanded={allKinds} onClick={() => setAllKinds(!allKinds)}>
          {allKinds ? t('entity.template.facts.showFewer') : t('entity.template.area.allKinds', { count: kinds.length })}
        </button>
      )}
    </div>
  );
};

/** How many kinds of objects lie inside: a few fit a normal card, more need the full width. */
export const contentsKinds = (context: WidgetContext, property: ViewPropertyShape, untyped: string): number => {
  const area = context.model?.rows[setting(property, 'area') ?? ''];
  const layout = area ? areaLayout(area, context, untyped) : null;
  return layout ? countKinds(layout.objects).length : 0;
};

export const ContentsViewer: Widget = (props) => <Contents {...props} />;

/** The kinds and names of the objects inside, for "Find a fact". */
export const contentsText = (context: WidgetContext, property: ViewPropertyShape) => {
  const area = context.model?.rows[setting(property, 'area') ?? ''];
  const objects = area ? (areaLayout(area, context, '')?.objects ?? []) : [];
  return objects.map((object) => `${object.kind} ${object.name}`).join(' ');
};

/** Whether the dview:area row has objects inside it. */
export const hasContents = (context: WidgetContext, property: ViewPropertyShape) => {
  const area = context.model?.rows[setting(property, 'area') ?? ''];
  return Boolean(area && (areaLayout(area, context, '')?.objects.length ?? 0) > 0);
};

/** dview:TrackPeriodsViewer: every track under the structure with its period, when one has ended. */
const TrackPeriods = ({ property, context }: Parameters<Widget>[0]): JSX.Element | null => {
  const { t } = useTranslation();
  const area = context.model?.rows[setting(property, 'area') ?? ''];
  const layout = area ? areaLayout(area, context, '') : null;
  if (!layout || !layout.periods.some((period) => period.ended)) return null;
  const trackShape = context.model?.shapes?.nodeShapes.find((node) => node.targetClasses.includes(TRACK));
  const trackClass = trackShape ? pickLang(trackShape.label, context.language) : null;
  // Tracks with one name and one period (pieces of track 2 under a long tunnel) are one line
  const lines = new Map<string, (typeof layout.periods)[number] & { name: string; count: number }>();
  for (const period of layout.periods) {
    const name = recordName(period.iri, context, trackClass);
    const key = `${name} ${period.begin} ${period.end}`;
    const line = lines.get(key);
    if (line) line.count += 1;
    else lines.set(key, { ...period, name, count: 1 });
  }
  const sorted = [...lines.values()].sort(
    (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }) || (a.begin ?? 0) - (b.begin ?? 0)
  );
  return (
    <ul className={styles.periods}>
      {sorted.map((line) => (
        <li key={`${line.iri} ${line.begin}`} data-ended={line.ended || undefined}>
          <UriLink uri={line.iri} context={context}>
            {line.name}
          </UriLink>
          {line.count > 1 && <span className={styles.periodEnded}>×{line.count}</span>}
          <span className={styles.periodDates}>
            {line.beginDate ?? '…'} → {line.endDate ?? t('entity.template.area.open')}
          </span>
          {line.ended && <span className={styles.periodEnded}>{t('entity.template.area.ended')}</span>}
        </li>
      ))}
    </ul>
  );
};

export const TrackPeriodsViewer: Widget = (props) => <TrackPeriods {...props} />;

/** Whether a track under the dview:area row has ended, so the periods are worth listing. */
export const hasEndedTracks = (context: WidgetContext, property: ViewPropertyShape) => {
  const area = context.model?.rows[setting(property, 'area') ?? ''];
  return Boolean(area && areaLayout(area, context, '')?.periods.some((period) => period.ended));
};
